import { useEffect, useId } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  useCreateProviderWebhook,
  useUpdateProviderWebhook,
} from '@/hooks/provider/useProviderWebhooks';
import {
  PROVIDER_WEBHOOK_EVENTS,
  type ProviderWebhookEndpoint,
  type UpsertProviderWebhookInput,
} from '@/types/provider-webhooks';
import { ProviderWebhooksRequestError } from '@/services/provider/provider-webhooks.service';

const schema = z.object({
  name: z.string().min(1).max(255),
  url: z.string().url(),
  secret: z.string().optional().default(''),
  clearSecret: z.boolean().default(false),
  headersJson: z.string().optional().default(''),
  clearHeaders: z.boolean().default(false),
  subscribedEvents: z.array(z.enum(PROVIDER_WEBHOOK_EVENTS)).min(1, 'Select at least one event.'),
  timeout: z.coerce.number().int().min(1).max(120),
  enabled: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

interface ProviderWebhookConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  webhook?: ProviderWebhookEndpoint | null;
}

export function ProviderWebhookConfigDialog({
  open,
  onOpenChange,
  mode,
  webhook,
}: ProviderWebhookConfigDialogProps) {
  const { t } = useTranslation();
  const eventsFieldId = useId();
  const createMutation = useCreateProviderWebhook();
  const updateMutation = useUpdateProviderWebhook();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      url: '',
      secret: '',
      clearSecret: false,
      headersJson: '',
      clearHeaders: false,
      subscribedEvents: [],
      timeout: 10,
      enabled: true,
    },
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    if (webhook) {
      form.reset({
        name: webhook.name,
        url: webhook.url,
        secret: '',
        clearSecret: false,
        headersJson: '',
        clearHeaders: false,
        subscribedEvents: webhook.subscribedEvents,
        timeout: webhook.timeout,
        enabled: webhook.enabled,
      });
      return;
    }

    form.reset({
      name: '',
      url: '',
      secret: '',
      clearSecret: false,
      headersJson: '',
      clearHeaders: false,
      subscribedEvents: [],
      timeout: 10,
      enabled: true,
    });
  }, [form, open, webhook]);

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const clearSecret = form.watch('clearSecret');
  const clearHeaders = form.watch('clearHeaders');

  const title = mode === 'create'
    ? t('settings.webhooks.createDialogTitle', 'Add webhook endpoint')
    : t('settings.webhooks.editDialogTitle', 'Edit webhook endpoint');

  const submitLabel = mode === 'create'
    ? t('settings.webhooks.createDialogCta', 'Create webhook')
    : t('settings.webhooks.updateDialogCta', 'Save changes');

  const applyFieldErrors = (error: ProviderWebhooksRequestError): boolean => {
    if (!error.errors) {
      return false;
    }

    let didApply = false;
    const fieldMap: Record<string, keyof FormValues> = {
      name: 'name',
      url: 'url',
      secret: 'secret',
      headers: 'headersJson',
      subscribed_events: 'subscribedEvents',
      timeout: 'timeout',
      enabled: 'enabled',
    };

    for (const [serverField, messages] of Object.entries(error.errors)) {
      const formField = fieldMap[serverField];
      if (!formField || !Array.isArray(messages) || messages.length === 0) {
        continue;
      }
      form.setError(formField, { type: 'server', message: messages[0] });
      didApply = true;
    }

    return didApply;
  };

  const onSubmit = async (values: FormValues) => {
    const payload: UpsertProviderWebhookInput = {
      name: values.name.trim(),
      url: values.url.trim(),
      subscribedEvents: values.subscribedEvents,
      timeout: values.timeout,
      enabled: values.enabled,
    };

    const secret = values.secret?.trim() ?? '';
    const rawHeaders = values.headersJson?.trim() ?? '';

    if (mode === 'edit') {
      if (values.clearHeaders) {
        payload.headers = {};
      } else if (rawHeaders.length > 0) {
        try {
          const parsed = JSON.parse(rawHeaders);
          if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            throw new Error('invalid_headers');
          }

          payload.headers = Object.entries(parsed as Record<string, unknown>).reduce<Record<string, string>>(
            (acc, [key, value]) => {
              if (typeof value === 'string') {
                acc[key] = value;
              } else if (value !== undefined && value !== null) {
                acc[key] = String(value);
              }
              return acc;
            },
            {},
          );
        } catch {
          form.setError('headersJson', {
            type: 'manual',
            message: t('settings.webhooks.form.headersJsonError', 'Enter a valid JSON object for headers.'),
          });
          return;
        }
      }
    } else if (rawHeaders.length > 0) {
      try {
        const parsed = JSON.parse(rawHeaders);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new Error('invalid_headers');
        }

        payload.headers = Object.entries(parsed as Record<string, unknown>).reduce<Record<string, string>>(
          (acc, [key, value]) => {
            if (typeof value === 'string') {
              acc[key] = value;
            } else if (value !== undefined && value !== null) {
              acc[key] = String(value);
            }
            return acc;
          },
          {},
        );
      } catch {
        form.setError('headersJson', {
          type: 'manual',
          message: t('settings.webhooks.form.headersJsonError', 'Enter a valid JSON object for headers.'),
        });
        return;
      }
    }

    if (mode === 'create') {
      if (secret.length > 0) {
        payload.secret = secret;
      }
    } else if (values.clearSecret) {
      payload.secret = '';
    } else if (secret.length > 0) {
      payload.secret = secret;
    }

    try {
      if (mode === 'create') {
        await createMutation.mutateAsync(payload);
      } else if (webhook) {
        await updateMutation.mutateAsync({ webhookId: webhook.id, input: payload });
      }

      toast.success(
        mode === 'create'
          ? t('settings.webhooks.createSuccess', 'Webhook endpoint created.')
          : t('settings.webhooks.updateSuccess', 'Webhook endpoint updated.'),
      );
      onOpenChange(false);
    } catch (error) {
      if (error instanceof ProviderWebhooksRequestError && applyFieldErrors(error)) {
        toast.error(error.message);
        return;
      }

      toast.error(
        error instanceof Error
          ? error.message
          : t('settings.webhooks.saveError', 'Unable to save webhook endpoint.'),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {t(
              'settings.webhooks.dialogDescription',
              'Configure where Florentin will deliver selected webhook events.',
            )}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('settings.webhooks.form.nameLabel', 'Name')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('settings.webhooks.form.namePlaceholder', 'Primary endpoint')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('settings.webhooks.form.urlLabel', 'Endpoint URL')}</FormLabel>
                  <FormControl>
                    <Input placeholder="https://hooks.example.com/florentin" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="secret"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('settings.webhooks.form.secretLabel', 'Signing secret')}</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder={t(
                        'settings.webhooks.form.secretPlaceholder',
                        'Enter a secret to sign webhook payloads',
                      )}
                      {...field}
                      disabled={clearSecret}
                    />
                  </FormControl>
                  {mode === 'edit' && webhook?.hasSecret ? (
                    <FormDescription>
                      {t('settings.webhooks.form.secretConfigured', 'A secret is currently configured.')}
                    </FormDescription>
                  ) : (
                    <FormDescription>
                      {t(
                        'settings.webhooks.form.secretDescription',
                        'Optional. Leave blank to keep the current secret on update.',
                      )}
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {mode === 'edit' ? (
              <FormField
                control={form.control}
                name="clearSecret"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-1">
                      <FormLabel className="text-base">
                        {t('settings.webhooks.form.clearSecretLabel', 'Clear stored secret')}
                      </FormLabel>
                      <FormDescription>
                        {t(
                          'settings.webhooks.form.clearSecretDescription',
                          'When enabled, Florentin will remove the existing secret for this endpoint.',
                        )}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            ) : null}

            <FormField
              control={form.control}
              name="headersJson"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('settings.webhooks.form.headersLabel', 'Custom headers (JSON)')}</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={4}
                      placeholder='{"X-Webhook-Token":"your-static-token"}'
                      {...field}
                      disabled={clearHeaders}
                    />
                  </FormControl>
                  {mode === 'edit' && webhook?.hasCustomHeaders ? (
                    <FormDescription>
                      {t(
                        'settings.webhooks.form.headersConfigured',
                        'Custom headers are currently configured (values are hidden).',
                      )}{' '}
                      {webhook.customHeaderKeys.length > 0
                        ? t('settings.webhooks.form.headerKeys', 'Keys: {{keys}}', {
                            keys: webhook.customHeaderKeys.join(', '),
                          })
                        : null}
                    </FormDescription>
                  ) : (
                    <FormDescription>
                      {t(
                        'settings.webhooks.form.headersDescription',
                        'Optional. Add static headers for outbound requests (for example n8n header auth).',
                      )}
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {mode === 'edit' ? (
              <FormField
                control={form.control}
                name="clearHeaders"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-1">
                      <FormLabel className="text-base">
                        {t('settings.webhooks.form.clearHeadersLabel', 'Clear custom headers')}
                      </FormLabel>
                      <FormDescription>
                        {t(
                          'settings.webhooks.form.clearHeadersDescription',
                          'When enabled, Florentin will remove all stored custom headers for this endpoint.',
                        )}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            ) : null}

            <FormField
              control={form.control}
              name="subscribedEvents"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('settings.webhooks.form.eventsLabel', 'Subscribed events')}</FormLabel>
                  <FormControl>
                    <div id={eventsFieldId} className="space-y-3 rounded-lg border p-4">
                      {PROVIDER_WEBHOOK_EVENTS.map((eventName) => {
                        const checked = field.value.includes(eventName);
                        return (
                          <div key={eventName} className="flex items-center gap-3">
                            <Checkbox
                              id={`${eventsFieldId}-${eventName}`}
                              checked={checked}
                              onCheckedChange={(next) => {
                                const isChecked = next === true;
                                if (isChecked) {
                                  field.onChange([...field.value, eventName]);
                                } else {
                                  field.onChange(field.value.filter((value) => value !== eventName));
                                }
                              }}
                            />
                            <Label htmlFor={`${eventsFieldId}-${eventName}`} className="cursor-pointer font-normal">
                              {t(`settings.webhooks.events.${eventName}`, eventName)}
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="timeout"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('settings.webhooks.form.timeoutLabel', 'Timeout (seconds)')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        max={120}
                        value={String(field.value)}
                        onChange={(event) => field.onChange(event.target.value)}
                      />
                    </FormControl>
                    <FormDescription>
                      {t(
                        'settings.webhooks.form.timeoutDescription',
                        'Requests are aborted when no response is received within this window.',
                      )}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="enabled"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-1">
                      <FormLabel className="text-base">
                        {t('settings.webhooks.form.enabledLabel', 'Enabled')}
                      </FormLabel>
                      <FormDescription>
                        {t(
                          'settings.webhooks.form.enabledDescription',
                          'Disabled endpoints remain saved but will not receive events.',
                        )}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {submitLabel}
                  </>
                ) : (
                  submitLabel
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
